/** Transport perspective routes, layer model, and demo-only mock fallbacks. */

export const TRANSPORT_BASE_PATH = "/transport";

export const TRANSPORT_DEMO_MODE =
  String(process.env.REACT_APP_TRANSPORT_DEMO_MODE || "").toLowerCase() === "true";

export const TRANSPORT_SECTIONS = {
  DASHBOARD: "dashboard",
  MAP: "map",
  TRANSIT: "transit",
  ROUTES: "routes",
  NETWORK: "network",
  DISRUPTIONS: "disruptions",
  ANALYTICS: "analytics",
  /** Vehicle pricing (formerly standalone Automotive perspective). */
  VEHICLES: "vehicles",
  MAKES: "makes",
  MODELS: "models",
  VEHICLE_SOURCES: "vehicle-sources",
  VEHICLE_SOURCE_REVIEW: "vehicle-source-review",
};

/** Merged Mobility perspective — transport network + vehicle market intelligence. */
export const MOBILITY_PERSPECTIVE_LABEL = "Mobility";
export const MOBILITY_REGION_LABEL = "Antioquia, Colombia";

/** Legacy URLs — redirect to Mobility Map (demo baseline). */
export const TRANSPORT_LEGACY_SECTIONS = Object.freeze([
  TRANSPORT_SECTIONS.TRANSIT,
  TRANSPORT_SECTIONS.ROUTES,
  TRANSPORT_SECTIONS.NETWORK,
]);

export function isTransportLegacySection(section) {
  return TRANSPORT_LEGACY_SECTIONS.includes(section);
}

export const TRANSPORT_SECTION_PATHS = {
  [TRANSPORT_SECTIONS.DASHBOARD]: TRANSPORT_BASE_PATH,
  [TRANSPORT_SECTIONS.MAP]: `${TRANSPORT_BASE_PATH}/map`,
  [TRANSPORT_SECTIONS.TRANSIT]: `${TRANSPORT_BASE_PATH}/transit`,
  [TRANSPORT_SECTIONS.ROUTES]: `${TRANSPORT_BASE_PATH}/routes`,
  [TRANSPORT_SECTIONS.NETWORK]: `${TRANSPORT_BASE_PATH}/network`,
  [TRANSPORT_SECTIONS.DISRUPTIONS]: `${TRANSPORT_BASE_PATH}/disruptions`,
  [TRANSPORT_SECTIONS.ANALYTICS]: `${TRANSPORT_BASE_PATH}/analytics`,
  [TRANSPORT_SECTIONS.VEHICLES]: `${TRANSPORT_BASE_PATH}/vehicles`,
  [TRANSPORT_SECTIONS.MAKES]: `${TRANSPORT_BASE_PATH}/makes`,
  [TRANSPORT_SECTIONS.MODELS]: `${TRANSPORT_BASE_PATH}/models`,
  [TRANSPORT_SECTIONS.VEHICLE_SOURCES]: `${TRANSPORT_BASE_PATH}/vehicle-sources`,
  [TRANSPORT_SECTIONS.VEHICLE_SOURCE_REVIEW]: `${TRANSPORT_BASE_PATH}/vehicle-source-review`,
};

/** Map legacy /automotive paths to mobility sections. */
export const AUTOMOTIVE_SECTION_REDIRECTS = {
  dashboard: TRANSPORT_SECTIONS.VEHICLES,
  makes: TRANSPORT_SECTIONS.MAKES,
  models: TRANSPORT_SECTIONS.MODELS,
  sources: TRANSPORT_SECTIONS.VEHICLE_SOURCES,
};

/** Placeholder copy for transport workspace sections not yet built out. */
export const TRANSPORT_SECTION_PLACEHOLDERS = {
  [TRANSPORT_SECTIONS.TRANSIT]: {
    title: "Transit network on the map",
    body: "Metro stations and lines are best explored on the Mobility Map. Turn on Metro layers and tap any station for details.",
    ctaSection: TRANSPORT_SECTIONS.MAP,
    ctaLabel: "Open Mobility Map",
  },
  [TRANSPORT_SECTIONS.ROUTES]: {
    title: "Bus routes on the map",
    body: "Official GeoMedellín bus stops and corridors are on the Mobility Map. Trip planning is not available.",
    ctaSection: TRANSPORT_SECTIONS.MAP,
    ctaLabel: "Open Mobility Map",
  },
  [TRANSPORT_SECTIONS.DISRUPTIONS]: {
    title: "Disruption Risk",
    body: "Weather-linked derived disruption signals appear here when the signal derivation job has run.",
  },
  [TRANSPORT_SECTIONS.ANALYTICS]: {
    title: "Ridership Analytics",
    body: "Metro Afluencia analytics are available on this page when ridership data has been ingested.",
  },
};

/** Short disclaimer shown in Transport UI chrome. */
export const TRANSPORT_QUICK_DISCLAIMER =
  "Official static and derived mobility sources — not live service status and not a trip planner.";

export const MOBILITY_VEHICLE_DISCLAIMER =
  "Guide values, marketplace asks, and repair benchmarks are indicative — not live dealer quotes, closed sales, or shop invoices.";

export const TRANSPORT_SOURCE_TRUST_LABELS = {
  officialStatic: "Official static data",
  communityMaintained: "Community-maintained data",
  derivedSignal: "Derived signal",
  manualReport: "Manual/community report",
  historical: "Historical/latest-available data",
  notLive: "Not live service status",
  notTripPlanner: "Not a trip planner",
  needsAttention: "Needs attention",
  notConfigured: "Not configured",
  stale: "Stale",
  fresh: "Fresh",
  partial: "Partial",
  loaded: "Loaded",
  demoFixture: "Demo fixture",
};

export const TRANSPORT_FRESHNESS_LABELS = {
  fresh: "Fresh",
  partial: "Partial",
  stale: "Stale",
  not_configured: "Not configured",
  degraded: "Needs attention",
  demo: "Demo data",
};

export const TRANSPORT_SOURCE_STATUS_COPY = {
  healthy: "Loaded",
  success: "Loaded",
  partial_success: "Partially loaded",
  degraded: "Needs attention",
  stale: "Stale",
  not_configured: "Not configured",
  discovery_pending: "Source pending",
  stub_only: "Stub only",
  failed: "Failed",
  failing: "Failed",
  disabled: "Disabled",
  unknown: "Unknown",
};

/** Single source of truth for context panel + dashboard navigation. */
export const TRANSPORT_TOOLS = [
  {
    id: TRANSPORT_SECTIONS.DASHBOARD,
    icon: "📋",
    label: "Overview",
    desc: "Vehicle market health, alerts, and historic signals",
    countKey: null,
  },
  {
    id: TRANSPORT_SECTIONS.VEHICLES,
    icon: "⛟",
    label: "Vehicle Market",
    desc: "Search listings from approved marketplace and dealer sources",
    countKey: "activeVehicles",
  },
  {
    id: "vehicle-analytics",
    icon: "📊",
    label: "Market analytics",
    desc: "Class supply, pricing, and price-drop trends",
    countKey: null,
    path: () => `${TRANSPORT_SECTION_PATHS[TRANSPORT_SECTIONS.VEHICLES]}/analytics`,
    activeMatch: (pathname) => pathname.includes("/vehicles/analytics"),
  },
  {
    id: TRANSPORT_SECTIONS.MAKES,
    icon: "🏷",
    label: "By make",
    desc: "Browse catalog makes and guide trends",
    countKey: null,
  },
  {
    id: TRANSPORT_SECTIONS.VEHICLE_SOURCES,
    icon: "🔌",
    label: "Vehicle sources",
    desc: "Source inventory, sync health, and compliance",
    countKey: null,
  },
  {
    id: TRANSPORT_SECTIONS.MAP,
    icon: "🗺",
    label: "Mobility Map",
    desc: "Explore Metro, bus, and mobility layers",
    countKey: "totalMapFeatures",
  },
  {
    id: TRANSPORT_SECTIONS.ANALYTICS,
    icon: "📈",
    label: "Ridership",
    desc: "Historical Metro pressure — not live crowding",
    countKey: null,
  },
  {
    id: TRANSPORT_SECTIONS.DISRUPTIONS,
    icon: "⚠️",
    label: "Risks & Disruptions",
    desc: "Derived weather and mobility friction",
    countKey: "disruptionSignals",
  },
  {
    id: TRANSPORT_SECTIONS.MODELS,
    icon: "🔎",
    label: "Models & trims",
    desc: "Compare guide, market ask, and repair bands",
    countKey: null,
  },
  {
    id: TRANSPORT_SECTIONS.VEHICLE_SOURCE_REVIEW,
    icon: "✓",
    label: "Source review",
    desc: "Robots, terms, and access compliance before ingestion",
    countKey: null,
  },
];

/** Vehicle-market-first navigation for the Mobility context panel. */
export const TRANSPORT_PRIMARY_TOOLS = TRANSPORT_TOOLS.filter((tool) =>
  [
    TRANSPORT_SECTIONS.DASHBOARD,
    TRANSPORT_SECTIONS.VEHICLES,
    "vehicle-analytics",
    TRANSPORT_SECTIONS.MAKES,
    TRANSPORT_SECTIONS.VEHICLE_SOURCES,
  ].includes(tool.id)
);

/** Transit and supporting mobility views in the context panel. */
export const TRANSPORT_SECONDARY_TOOLS = TRANSPORT_TOOLS.filter((tool) =>
  [
    TRANSPORT_SECTIONS.MAP,
    TRANSPORT_SECTIONS.ANALYTICS,
    TRANSPORT_SECTIONS.DISRUPTIONS,
    TRANSPORT_SECTIONS.MODELS,
    TRANSPORT_SECTIONS.VEHICLE_SOURCE_REVIEW,
  ].includes(tool.id)
);

/** Dashboard section groupings for the overview page. */
export const TRANSPORT_DASHBOARD_GROUPS = {
  network: {
    title: "Transit network",
    subtitle: "Official static Metro and GeoMedellín bus data on the map",
    mapSection: TRANSPORT_SECTIONS.MAP,
  },
  ridership: {
    title: "Ridership pressure",
    subtitle:
      "Latest available Metro afluencia — historical source data, not live crowding or service status",
    section: TRANSPORT_SECTIONS.ANALYTICS,
  },
  risks: {
    title: "Risks & friction",
    subtitle:
      "Potential mobility risks derived from Weather and transport source data — not official service status",
    section: TRANSPORT_SECTIONS.DISRUPTIONS,
  },
  access: {
    title: "Access & neighborhoods",
    subtitle: "Estimated scores — not government ratings or commute guarantees",
    mapSection: TRANSPORT_SECTIONS.MAP,
  },
  vehicleMarket: {
    title: "Vehicle Market",
    subtitle:
      "Listings, guide values, and class analytics from approved marketplace and dealer sources",
    section: TRANSPORT_SECTIONS.VEHICLES,
  },
};

/** Demo-only KPIs — never shown unless REACT_APP_TRANSPORT_DEMO_MODE=true */
export const TRANSPORT_DEMO_SUMMARY = {
  activeRoutes: 42,
  metroLines: 6,
  metroStations: 28,
  busStops: 1200,
  busRoutes: 42,
  disruptionSignals: 4,
  communityMobilityReports: 12,
  totalMapFeatures: 2400,
  serviceAlerts: 3,
  onTimeRate: 87,
  dailyRiders: "1.2M",
  roadIncidents: 5,
};

/** Demo ridership periods for overview trend sparklines. */
export const TRANSPORT_DEMO_RIDERSHIP_TRENDS = [
  { observationDate: "2024-08", totalPassengers: 892000 },
  { observationDate: "2024-09", totalPassengers: 918000 },
  { observationDate: "2024-10", totalPassengers: 945000 },
  { observationDate: "2024-11", totalPassengers: 962000 },
  { observationDate: "2024-12", totalPassengers: 988000 },
  { observationDate: "2025-01", totalPassengers: 1012000 },
];

/** Collapsed layer groups for Mobility Map demo (Milestone 3). */
export const TRANSPORT_MAP_LAYER_GROUPS = [
  {
    id: "transitNetwork",
    label: "Transit Network",
    note: "Official Metro and GeoMedellín static data",
    defaultExpanded: true,
    tier: 0,
  },
  {
    id: "risksDisruptions",
    label: "Risks & Disruptions",
    note: "Derived weather-related mobility risks near transit assets",
    defaultExpanded: false,
    tier: 1,
    unavailableHint:
      "Run npm run derive:transport:weather-signals after Weather and Tier 0 transport data are loaded.",
  },
  {
    id: "intelligence",
    label: "Intelligence",
    note: "Historical Metro ridership pressure — not live crowding",
    defaultExpanded: false,
    tier: 1,
    unavailableHint:
      "Run Metro afluencia ingestion to enable ridership pressure on the map. Access scores activate in a later milestone.",
  },
  {
    id: "communityReference",
    label: "Community / Reference",
    note: "OpenStreetMap community-maintained transport — optional context",
    defaultExpanded: false,
    tier: 1,
    unavailableHint:
      "Run OSM transport ingestion to enable. Community-maintained — may differ from official Metro/GeoMedellín data.",
  },
];

/** @deprecated Use TRANSPORT_MAP_LAYER_GROUPS */
export const TRANSPORT_LAYER_GROUPS = TRANSPORT_MAP_LAYER_GROUPS;

export const TRANSPORT_SOURCE_DISPLAY_NAMES = {
  METRO_MEDELLIN_GTFS: "Metro de Medellín GTFS",
  GEOMEDELLIN_TRANSPORT_STOPS: "GeoMedellín Public Transport Stops",
  GEOMEDELLIN_TRANSPORT_ROUTES: "GeoMedellín Public Transport Routes",
  OSM_TRANSPORT: "OpenStreetMap Transport",
};

export const TIER0_TRANSPORT_LAYER_IDS = [
  "transportMetroStations",
  "transportMetroLines",
  "transportBusStops",
  "transportBusRoutes",
];

const LAYER_ID_TO_COUNT_KEY = {
  transportMetroStations: "metroStations",
  transportMetroLines: "metroLines",
  transportBusStops: "busStops",
  transportBusRoutes: "busRoutes",
};

export const TRANSPORT_LAYER_OPTIONS = [
  {
    id: "transportMetroStations",
    group: "transitNetwork",
    label: "Metro stations",
    desc: "Official Metro de Medellín static stations",
    featureTypes: ["metro_station"],
    defaultVisible: true,
    minZoom: 10,
    sourceTypeLabel: "official",
  },
  {
    id: "transportMetroLines",
    group: "transitNetwork",
    label: "Metro lines",
    desc: "Official Metro line geometry",
    featureTypes: ["metro_line"],
    defaultVisible: true,
    minZoom: 9,
    sourceTypeLabel: "official",
  },
  {
    id: "transportBusStops",
    group: "transitNetwork",
    label: "Bus stops",
    desc: "Official GeoMedellín bus paradas",
    featureTypes: ["bus_stop"],
    defaultVisible: true,
    minZoom: 13,
    sourceTypeLabel: "official",
  },
  {
    id: "transportBusRoutes",
    group: "transitNetwork",
    label: "Bus routes",
    desc: "Official GeoMedellín bus corridors",
    featureTypes: ["bus_route"],
    defaultVisible: true,
    minZoom: 11,
    sourceTypeLabel: "official",
  },
  {
    id: "transportOsmCommunity",
    group: "communityReference",
    label: "Community-maintained OSM layer",
    desc: "OpenStreetMap transport features — may differ from official data",
    featureTypes: [
      "osm_bus_stop",
      "osm_transport_station",
      "osm_rail_station",
      "osm_transport_platform",
      "osm_stop_position",
      "osm_transport_line",
      "osm_bus_route",
      "osm_cable_line",
      "osm_transport_relation",
    ],
    defaultVisible: false,
    minZoom: 11,
    sourceTypeLabel: "community",
  },
  {
    id: "transportDiagnostics",
    group: "communityReference",
    label: "Diagnostics (gaps & duplicates)",
    desc: "Likely duplicates, possible duplicates, and possible gaps",
    featureTypes: ["transport_diagnostic"],
    defaultVisible: false,
    minZoom: 11,
    sourceTypeLabel: "derived",
  },
  {
    id: "transportMetroRidershipPressure",
    group: "intelligence",
    label: "Ridership pressure (Metro)",
    desc: "Historical station pressure from latest afluencia period — not live crowding",
    featureTypes: ["metro_ridership_pressure"],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "official",
  },
  {
    id: "transportDisruptionSignals",
    group: "risksDisruptions",
    label: "Weather-related mobility risks",
    desc: "Potential weather-related mobility risks — not official service status",
    featureTypes: [
      "transport_weather_disruption_signal",
      "transport_flood_risk_signal",
      "transport_rain_risk_signal",
      "transport_alert_near_station",
      "transport_alert_near_route",
    ],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "derived",
  },
  {
    id: "transportDisruptionWeatherAlerts",
    group: "risksDisruptions",
    label: "Weather Alerts Near Transport",
    desc: "Active/recent weather alerts near stations, stops, or routes",
    featureTypes: [
      "transport_alert_near_station",
      "transport_alert_near_route",
      "transport_weather_disruption_signal",
    ],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "derived",
  },
  {
    id: "transportDisruptionFloodContext",
    group: "risksDisruptions",
    label: "Flood-Prone Context",
    desc: "Contextual flood exposure near stops and stations",
    featureTypes: ["transport_flood_risk_signal"],
    defaultVisible: false,
    minZoom: 11,
    sourceTypeLabel: "derived",
  },
  {
    id: "transportDisruptionRainRisk",
    group: "risksDisruptions",
    label: "Rain / Sensor Risk",
    desc: "Recent rainfall or sensor threshold signals near corridors",
    featureTypes: ["transport_rain_risk_signal"],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "derived",
  },
  {
    id: "transportCommunityMobilityReports",
    group: "risksDisruptions",
    label: "Community mobility reports",
    desc: "Local mobility signals from community submissions — not official status",
    featureTypes: [
      "community_mobility_report",
      "community_road_blocked_report",
      "community_flooded_crossing_report",
      "community_transit_stop_issue",
      "community_station_access_issue",
    ],
    defaultVisible: false,
    minZoom: 11,
    sourceTypeLabel: "community",
  },
  {
    id: "transportCommunityVerifiedReports",
    group: "risksDisruptions",
    label: "Verified Reports",
    desc: "Community reports marked verified by moderators",
    featureTypes: [
      "community_mobility_report",
      "community_road_blocked_report",
      "community_flooded_crossing_report",
      "community_transit_stop_issue",
      "community_station_access_issue",
    ],
    filterVerified: true,
    defaultVisible: false,
    minZoom: 11,
    sourceTypeLabel: "community",
  },
  {
    id: "transportCommunityUnverifiedReports",
    group: "risksDisruptions",
    label: "Unverified Reports",
    desc: "Submitted or under-review community reports",
    featureTypes: [
      "community_mobility_report",
      "community_road_blocked_report",
      "community_flooded_crossing_report",
      "community_transit_stop_issue",
      "community_station_access_issue",
    ],
    filterUnverified: true,
    defaultVisible: false,
    minZoom: 11,
    sourceTypeLabel: "community",
  },
  {
    id: "transportOfficialMetroAlerts",
    group: "risksDisruptions",
    label: "Official Metro Service Alerts",
    desc: "Published Metro notices — not live delay data",
    featureTypes: ["official_metro_service_alert", "official_transport_service_alert"],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "official",
  },
  {
    id: "transportOfficialMobilityNotices",
    group: "risksDisruptions",
    label: "Official Mobility Notices",
    desc: "City/metro mobility bulletins when ingested",
    featureTypes: ["official_transport_service_alert"],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "official",
  },
  {
    id: "transportManualOfficialNotices",
    group: "risksDisruptions",
    label: "Manual Official Notices",
    desc: "Manually entered from linked official source",
    featureTypes: ["manual_official_transport_notice"],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "manual",
  },
  {
    id: "transportOfficialRoadAlerts",
    group: "risksDisruptions",
    label: "Official Road Authority Alerts",
    desc: "Published road authority notices",
    featureTypes: ["official_road_alert"],
    defaultVisible: false,
    minZoom: 9,
    sourceTypeLabel: "official",
  },
  {
    id: "transportThirdPartyStatusCandidates",
    group: "risksDisruptions",
    label: "Third-Party Status (candidates)",
    desc: "License review required — off by default",
    featureTypes: ["third_party_transport_status_candidate"],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "third_party",
  },
  {
    id: "transportLiveVehiclePositions",
    group: "risksDisruptions",
    label: "Live Vehicle Positions",
    desc: "Fresh positions from reviewed GTFS-RT — stale hidden",
    featureTypes: ["realtime_vehicle_position"],
    defaultVisible: false,
    minZoom: 11,
    sourceTypeLabel: "official",
  },
  {
    id: "transportLiveServiceAlerts",
    group: "risksDisruptions",
    label: "Live Service Alerts (GTFS-RT)",
    desc: "GTFS-RT service alerts when configured",
    featureTypes: ["realtime_service_alert", "official_gtfs_rt_service_alert"],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "official",
  },
  {
    id: "transportRoadCorridors",
    group: "risksDisruptions",
    label: "Regional Road Corridors",
    desc: "Major highways and urban arterials",
    featureTypes: [
      "road_corridor",
      "highway_corridor",
      "regional_road",
      "concession_route",
      "urban_arterial",
      "access_road",
    ],
    defaultVisible: false,
    minZoom: 11,
    sourceTypeLabel: "mixed",
  },
  {
    id: "transportRoadClosures",
    group: "risksDisruptions",
    label: "Active Road Closures",
    desc: "Closures and lane restrictions — not official unless sourced",
    featureTypes: ["road_closure"],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "mixed",
  },
  {
    id: "transportRoadConstruction",
    group: "risksDisruptions",
    label: "Construction",
    desc: "Road construction zones",
    featureTypes: ["road_construction"],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "manual",
  },
  {
    id: "transportRoadAccidents",
    group: "risksDisruptions",
    label: "Accidents & Obstructions",
    desc: "Accidents and blocked corridors",
    featureTypes: ["road_accident", "road_obstruction"],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "mixed",
  },
  {
    id: "transportRoadFloodLandslide",
    group: "risksDisruptions",
    label: "Flood / Landslide (Road)",
    desc: "Flood or landslide affecting roads",
    featureTypes: ["road_flood_issue", "road_landslide_issue"],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "mixed",
  },
  {
    id: "transportManualRoadIncidents",
    group: "risksDisruptions",
    label: "Manual / Community Road",
    desc: "Admin or community road incidents",
    featureTypes: ["manual_road_incident", "community_road_report"],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "manual",
  },
  {
    id: "transportMobilityScorecards",
    group: "intelligence",
    label: "Mobility Score Polygons",
    desc: "Neighborhood scorecards — Midpoint intelligence, not official ratings",
    featureTypes: ["mobility_scorecard_polygon"],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "derived",
    includeMobilityScorecards: true,
  },
  {
    id: "transportMobilityLowAccess",
    group: "intelligence",
    label: "Low-Access Areas",
    desc: "Neighborhoods with low mobility score bands",
    featureTypes: ["mobility_scorecard_polygon"],
    defaultVisible: false,
    minZoom: 10,
    sourceTypeLabel: "derived",
    scoreBandFilter: "low",
    includeMobilityScorecards: true,
  },
];

export const TRANSPORT_FEATURE_TYPE_LABELS = {
  metro_station: "Metro station",
  metro_line: "Metro line",
  bus_stop: "Bus stop",
  bus_route: "Bus route",
  osm_bus_stop: "OSM bus stop",
  osm_transport_station: "OSM transport station",
  osm_rail_station: "OSM rail station",
  osm_transport_platform: "OSM platform",
  osm_stop_position: "OSM stop position",
  osm_transport_line: "OSM transport line",
  osm_bus_route: "OSM bus route",
  osm_cable_line: "OSM cable line",
  transport_diagnostic: "Comparison diagnostic",
  transport_weather_disruption_signal: "Weather disruption signal",
  transport_flood_risk_signal: "Flood context signal",
  transport_rain_risk_signal: "Rain risk signal",
  transport_alert_near_station: "Alert near station",
  transport_alert_near_route: "Alert near route",
  community_mobility_report: "Community mobility report",
  community_road_blocked_report: "Road blocked (community)",
  community_flooded_crossing_report: "Flooded crossing (community)",
  community_transit_stop_issue: "Transit stop issue (community)",
  community_station_access_issue: "Station access issue (community)",
  road_corridor: "Road corridor",
  highway_corridor: "Highway corridor",
  regional_road: "Regional road",
  concession_route: "Concession route",
  urban_arterial: "Urban arterial",
  access_road: "Access road",
  road_closure: "Road closure",
  road_construction: "Road construction",
  road_accident: "Road accident",
  road_obstruction: "Road obstruction",
  road_flood_issue: "Road flood issue",
  road_landslide_issue: "Road landslide",
  manual_road_incident: "Manual road incident",
  community_road_report: "Community road report",
  official_transport_service_alert: "Official service alert",
  official_metro_service_alert: "Official Metro service alert",
  official_bus_service_alert: "Official bus service alert",
  official_road_alert: "Official road alert",
  manual_official_transport_notice: "Manual official notice",
  third_party_transport_status_candidate: "Third-party status (candidate)",
  realtime_vehicle_position: "Live vehicle position",
  realtime_service_alert: "Live service alert (GTFS-RT)",
  official_gtfs_rt_service_alert: "Official GTFS-RT service alert",
  mobility_scorecard_polygon: "Mobility scorecard area",
};

export const TRANSPORT_SOURCE_TYPE_LABELS = {
  official: "Official static network data",
  community: "Community-maintained data",
  derived: "Derived signal",
  third_party: "Third-party source",
  manual: "Manual/community report",
  internal_manual: "Manual/admin entry",
};

export function getTransportSourceDisplayName(sourceKey, fallback = "") {
  if (!sourceKey) {
    return fallback || "Unknown source";
  }
  return TRANSPORT_SOURCE_DISPLAY_NAMES[sourceKey] || fallback || sourceKey;
}

export function getTransportLayerOption(layerId) {
  return TRANSPORT_LAYER_OPTIONS.find((layer) => layer.id === layerId) || null;
}

export function transportLayerHasMapData(layerId, layerCounts = {}, summaryCounts = {}) {
  const direct = layerCounts[layerId];
  if (typeof direct === "number") {
    return direct > 0;
  }
  const summaryKey = LAYER_ID_TO_COUNT_KEY[layerId];
  if (summaryKey && typeof summaryCounts[summaryKey] === "number") {
    return summaryCounts[summaryKey] > 0;
  }
  return false;
}

export function groupHasAnyLayerData(groupId, layerCounts = {}, summaryCounts = {}) {
  return TRANSPORT_LAYER_OPTIONS.some(
    (layer) =>
      layer.group === groupId &&
      transportLayerHasMapData(layer.id, layerCounts, summaryCounts)
  );
}

export function buildDefaultTransportLayerVisibility(saved = {}) {
  return Object.fromEntries(
    TRANSPORT_LAYER_OPTIONS.map((layer) => [
      layer.id,
      saved[layer.id] !== undefined ? Boolean(saved[layer.id]) : layer.defaultVisible,
    ])
  );
}

export function buildTransportMapSummaryStrip(layerVisibility = {}, layerCounts = {}) {
  const activeLabels = TRANSPORT_LAYER_OPTIONS.filter(
    (layer) => layerVisibility[layer.id] && transportLayerHasMapData(layer.id, layerCounts)
  ).map((layer) => layer.label);

  if (!activeLabels.length) {
    return null;
  }

  const sourceLabels = [];
  if (
    (layerVisibility.transportMetroStations || layerVisibility.transportMetroLines) &&
    (transportLayerHasMapData("transportMetroStations", layerCounts, {}) ||
      transportLayerHasMapData("transportMetroLines", layerCounts, {}))
  ) {
    sourceLabels.push(TRANSPORT_SOURCE_DISPLAY_NAMES.METRO_MEDELLIN_GTFS);
  }
  if (
    (layerVisibility.transportBusStops || layerVisibility.transportBusRoutes) &&
    (transportLayerHasMapData("transportBusStops", layerCounts, {}) ||
      transportLayerHasMapData("transportBusRoutes", layerCounts, {}))
  ) {
    sourceLabels.push("GeoMedellín");
  }
  const sources = [...new Set(sourceLabels)];

  const layersPart =
    activeLabels.length <= 3
      ? activeLabels.join(", ")
      : `${activeLabels.length} layers on`;

  if (sources.length) {
    return `Showing: ${layersPart} · ${sources.join(", ")} · Static network data`;
  }

  return `Showing: ${layersPart} · Static network data`;
}

export function resolveTransportMapStatusMessage({
  mapboxTokenMissing = false,
  summary = null,
  sourceHealth = null,
  mapLoading = false,
  mapError = null,
} = {}) {
  if (mapboxTokenMissing) {
    return {
      tone: "error",
      title: "Mapbox is not configured",
      body: "Add REACT_APP_MAPBOX_ACCESS_TOKEN to client/.env.local to view the Mobility Map.",
    };
  }

  if (mapError) {
    return {
      tone: "error",
      title: "Mobility map data unavailable",
      body: mapError,
    };
  }

  if (mapLoading) {
    return {
      tone: "info",
      title: "Loading mobility map",
      body: "Fetching Metro and bus features for the current map area…",
    };
  }

  const counts = summary?.counts || {};
  const metroCount = counts.metroStations ?? 0;
  const busCount = counts.busStops ?? 0;
  const total = counts.totalMapFeatures ?? 0;

  if (total === 0) {
    return {
      tone: "warn",
      title: "No network data loaded",
      body: "No Metro or bus network data is loaded yet. Run the Tier 0 ingestion jobs to populate the Mobility Map.",
      adminHint: true,
    };
  }

  if (metroCount > 0 && busCount === 0) {
    return {
      tone: "info",
      title: "Metro data available",
      body: "Metro data is available. Bus stops/routes are not configured or have not been ingested yet.",
      adminHint: true,
    };
  }

  if (busCount > 0 && metroCount === 0) {
    return {
      tone: "info",
      title: "Bus data available",
      body: "Bus data is available. Metro GTFS data is not configured or has not been ingested yet.",
      adminHint: true,
    };
  }

  const degraded =
    sourceHealth?.sources?.filter((s) =>
      ["degraded", "failing", "not_configured"].includes(s.healthStatus)
    ) || [];

  if (degraded.length) {
    return {
      tone: "warn",
      title: "Some sources need attention",
      body: "Some Transport sources need attention. Available layers are still shown.",
      adminHint: true,
    };
  }

  return null;
}

export function featureMatchesTransportLayer(featureType, layerOption, properties = {}) {
  if (!featureType || !layerOption?.featureTypes?.length) {
    return false;
  }
  if (!layerOption.featureTypes.includes(featureType)) {
    return false;
  }
  if (layerOption.filterVerified) {
    return properties.isVerified === true || properties.status === "verified";
  }
  if (layerOption.filterUnverified) {
    return (
      !properties.isVerified &&
      ["submitted", "under_review", "needs_more_info"].includes(properties.status)
    );
  }
  return true;
}

export function parseTransportSectionFromPath(pathname = "") {
  if (!pathname.startsWith(TRANSPORT_BASE_PATH)) {
    return { section: null };
  }

  const remainder = pathname.slice(TRANSPORT_BASE_PATH.length).replace(/^\//, "");
  if (!remainder) {
    return { section: TRANSPORT_SECTIONS.DASHBOARD };
  }

  const segment = remainder.split("/")[0];
  const known = Object.values(TRANSPORT_SECTIONS);
  return {
    section: known.includes(segment) ? segment : TRANSPORT_SECTIONS.DASHBOARD,
  };
}

export function transportPathForSection(section) {
  return TRANSPORT_SECTION_PATHS[section] || TRANSPORT_BASE_PATH;
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/** User-facing vehicle listing detail under Vehicle Market. */
export function vehicleMarketDetailPath(vehicleListingId) {
  return `${TRANSPORT_SECTION_PATHS[TRANSPORT_SECTIONS.VEHICLES]}/${vehicleListingId}`;
}

export function parseVehicleListingIdFromPath(pathname = "") {
  const base = TRANSPORT_SECTION_PATHS[TRANSPORT_SECTIONS.VEHICLES];
  if (!pathname.startsWith(`${base}/`)) return null;
  const segment = pathname.slice(base.length + 1).split("/")[0];
  if (
    !segment ||
    segment === "filters" ||
    segment === "analytics" ||
    segment === "deals" ||
    segment === "alerts" ||
    segment === "saved-searches" ||
    segment === "depreciation"
  ) {
    return null;
  }
  return UUID_PATTERN.test(segment) ? segment : null;
}

export function vehicleMarketDealsPath() {
  return `${TRANSPORT_SECTION_PATHS[TRANSPORT_SECTIONS.VEHICLES]}/deals`;
}

export function vehicleMarketAlertsPath() {
  return `${TRANSPORT_SECTION_PATHS[TRANSPORT_SECTIONS.VEHICLES]}/alerts`;
}

export function vehicleMarketSavedSearchesPath() {
  return `${TRANSPORT_SECTION_PATHS[TRANSPORT_SECTIONS.VEHICLES]}/saved-searches`;
}

export function vehicleMarketAnalyticsPath() {
  return `${TRANSPORT_SECTION_PATHS[TRANSPORT_SECTIONS.VEHICLES]}/analytics`;
}

export function vehicleMarketDepreciationPath() {
  return `${TRANSPORT_SECTION_PATHS[TRANSPORT_SECTIONS.VEHICLES]}/depreciation`;
}

export function vehicleMarketAnalyticsClassPath(classKey) {
  return `${vehicleMarketAnalyticsPath()}/classes/${encodeURIComponent(classKey)}`;
}

export function parseVehicleMarketAnalyticsFromPath(pathname = "") {
  const base = TRANSPORT_SECTION_PATHS[TRANSPORT_SECTIONS.VEHICLES];
  const analyticsBase = `${base}/analytics`;
  if (pathname === analyticsBase || pathname === `${analyticsBase}/`) {
    return { view: "overview" };
  }
  const prefix = `${analyticsBase}/classes/`;
  if (pathname.startsWith(prefix)) {
    const classKey = decodeURIComponent(pathname.slice(prefix.length).split("/")[0] || "");
    if (classKey) return { view: "class", classKey: classKey.toUpperCase() };
  }
  return null;
}

export function isTransportDemoMode() {
  return TRANSPORT_DEMO_MODE;
}

export function formatTransportFeatureType(featureType) {
  return TRANSPORT_FEATURE_TYPE_LABELS[featureType] || featureType || "Transport feature";
}

export function formatTransportSourceType(sourceType) {
  return TRANSPORT_SOURCE_TYPE_LABELS[sourceType] || sourceType || "Unknown";
}
